import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  Chip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TimerRoundedIcon from "@mui/icons-material/TimerRounded";
import CRMCardDetailsDialog from "./CRMCardDetailsDialog";
import AddCRMCardDialog from "./AddCRMCardDialog";

const CRMCard = ({ card, handleDragStart, handleDeleteCard, handleOpenEditDialog, isDragOver = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  
  // Simple check: if card status is "Won" or "Lost", disable editing (similar to Done column)
  const isFinalized = card?.cardStatus === 'Won' || card?.cardStatus === 'Lost';
  
  // Menu handlers
  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // Edit
  const handleEdit = () => {
    handleOpenEditDialog(card);
    handleMenuClose();
  };

  // Delete
  const handleDelete = () => {
    handleDeleteCard(card.id);
    handleMenuClose();
  };

  // Open details dialog
  const handleCardClick = () => setOpenDetailsDialog(true);
  const handleDetailsDialogClose = () => setOpenDetailsDialog(false);

  // Get RAG status color
  const getRAGStatusColor = (ragStatus) => {
    switch (ragStatus) {
      case 'Red':
        return '#EF4444';
      case 'Amber':
        return '#F59E0B';
      case 'Green':
        return '#10B981';
      case 'Unassigned':
        return '#9E9E9E'; // Gray color for Unassigned
      default:
        return '#9E9E9E'; // Default to gray for Unassigned
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return '#f57c00';
      case 'Medium':
        return '#fbc02d';
      case 'Low':
        return '#388e3c';
      default:
        return '#757575';
    }
  };

  const template = card.templateData || {};
  const name = card.summary || template.name || 'N/A';
  const dealValue = template.deal_value || 0;
  const dealValueCurrency = template.deal_value_currency || 'USD';
  const ragStatus = template.rag || 'Unassigned';
  const priority = card.priority || template.priority;
  
  // Currency symbol mapping
  const currencySymbols = {
    'INR': '₹',
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'SGD': 'S$',
    'JPY': '¥'
  };
  const currencySymbol = currencySymbols[dealValueCurrency] || '$';

  return (
    <>
      <Paper
        data-card-id={card.id}
        sx={{
          mb: 2,
          p: 0,
          borderRadius: 2,
          backgroundColor: '#E8EAE6',
          position: 'relative',
          cursor: isFinalized ? 'default' : 'move',
          display: 'flex',
          flexDirection: 'column',
          transition: 'box-shadow 0.3s, transform 0.3s',
          border: isDragOver ? '2px dashed #1976d2' : 'none',
          opacity: isFinalized ? 0.7 : 1,
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
            transform: isFinalized ? 'none' : 'scale(1.02)',
          },
        }}
        draggable={!isFinalized}
        onDragStart={!isFinalized ? (event) => handleDragStart(event, card) : null}
      >
        {/* Top Section */}
        <Box
          sx={{
            backgroundColor: '#1976d2',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            height: '40px',
            width: "100%"
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', width: "40%"}}>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                display: 'block',
                lineHeight: '1',
                color: 'white',
                marginTop: '0.2rem'
              }}
            >
              {card.cardId || 'N/A'}
            </Typography>
          </Box>

          <Box display="flex" flexDirection="row" width="60%" justifyContent="flex-end" alignItems="center" gap={0.5}>
            {/* Priority Chip */}
            <Chip
              label={priority || 'N/A'}
              size="small"
              sx={{
                backgroundColor: getPriorityColor(priority),
                color: '#fff',
                fontSize: '0.75rem',
                borderRadius: '25px',
                padding: '1px 1px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
              }}
            />

            <IconButton
              onClick={handleMenuClick}
              disabled={isFinalized}
              sx={{
                color: 'text.secondary',
                padding: '0.5rem',
              }}
            >
              <MoreVertIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem onClick={handleEdit} disabled={isFinalized}>Edit</MenuItem>
              <MenuItem onClick={handleDelete} disabled={isFinalized}>Delete</MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Middle Section */}
        <Box         
          sx={{
            backgroundColor: '#87CEEB',
            flexGrow: 1,
            p: 1,
          }}
        >
          <Box onClick={handleCardClick} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
              {name}
            </Typography>
          </Box>
        </Box>

        {/* Bottom Section */}
        <Box
          sx={{
            backgroundColor: '#d3d3d3',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '20px',
          }}
        >
          {dealValue > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {currencySymbol}{dealValue.toLocaleString('en-IN')}
              </Typography>
            </Box>
          )}
          <Box display="flex" alignItems="center" gap={1}>
            {/* RAG Status Indicator */}
            <Tooltip title={`RAG Status: ${ragStatus}`} arrow>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  maxWidth: 12,
                  maxHeight: 12,
                  flexShrink: 0,
                  borderRadius: '2px',
                  backgroundColor: getRAGStatusColor(ragStatus),
                  border: '1px solid #fff',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                }}
              />
            </Tooltip>

            <Tooltip title={card.assigneeName || card?.templateData?.assigned_to || 'Unassigned'} arrow>
              <Avatar sx={{ bgcolor: 'green', width: 17, height: 17, fontSize: '0.70rem' }}>
                {(card.assigneeName || card?.templateData?.assigned_to || '?').charAt(0).toUpperCase()}
              </Avatar>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* CRM Card Details Dialog */}
      <CRMCardDetailsDialog
        open={openDetailsDialog}
        onClose={handleDetailsDialogClose}
        card={card}
        onEdit={handleOpenEditDialog}
      />
    </>
  );
};

export default CRMCard;

